import { memo, useEffect, useRef, useState } from "react";
import { FormControl } from "@mui/material";
import cn from "classnames";
import { useAppDispatch } from "@/store/hooks";
import { rephrase } from "@/store/qas/qasSlice";
import { useDebounce, useTranslate } from "@/hooks";
import { buttonRegexpPattern, splitButtonTemplatesIntoBlocks } from "@/helpers";
import { ButtonType } from "@/helpers/parsingChatMessageButtons";
import { IActionConfirmation, IActionPhrase } from "@/types/ses/sesItemConfigTypes";
import { CustomTextField } from "@/components/MUI";
import ButtonBlock from "./ButtonBlock/ButtonBlock";
import ButtonForm from "./ButtonForm/ButtonForm";
import Paraphrasing from "./Paraphrasing/Paraphrasing";
import InputAdornment from "./InputAdornment/InputAdornment";
import { IPhraseProps, TSaveHandlerProps } from "./Phrase.props";
import styles from "./Phrase.module.scss";

const Phrase = ({
	phrase,
	idxPhrase,
	arrayPhrases,
	channel,
	idxAction,
	isDisabled,
	multiline = true,
	showPhraseList,
	setShowPhraseList,
	handleSaveActionInStore,
}: IPhraseProps): JSX.Element => {
	const [inputPhrase, setInputPhrase] = useState<string>(phrase.replace(buttonRegexpPattern, "")); // голая фраза
	const [buttonsInPhrase, setButtonsInPhrase] = useState<string[][]>(splitButtonTemplatesIntoBlocks(phrase)); // кнопки во фразе
	const [maxRows, setMaxRows] = useState<number>(2); // max кол-во строк для показа фразы
	const inputRef = useRef<HTMLInputElement>(null); // ссылка на поле для ввода фразы

	const [anchorForButtonForm, setAnchorForButtonForm] = useState<HTMLElement | null>(null); // якорь для формы добавления/изменения кнопки
	const [buttonChangeFlg, setButtonChangeFlg] = useState<{ isChange: boolean; originalTemplate: string }>({ isChange: false, originalTemplate: "" }); // флаг изменения кнопки
	const [selectButtonType, setSelectButtonType] = useState<ButtonType>("button"); // тип кнопки
	const [inputButtonName, setInputButtonName] = useState<string>(""); // название кнопки
	const [inputButtonData, setInputButtonData] = useState<string>(""); // данные кнопки

	const [anchorForParaphrase, setAnchorForParaphrase] = useState<HTMLElement | null>(null); // якорь для блока перефразированного текста

	const dispatch = useAppDispatch();

	const translate = useTranslate(); // hook для перевода текста
	const debouncedInputPhrase = useDebounce(inputPhrase, 500); // задержка обработки фразы

	// следим за фразой
	useEffect(() => {
		setInputPhrase(phrase.replace(buttonRegexpPattern, ""));
		setButtonsInPhrase(splitButtonTemplatesIntoBlocks(phrase));
	}, [phrase]);

	// следим за открытием полного списка фраз
	useEffect(() => {
		if (showPhraseList && idxPhrase === 0) {
			inputRef.current?.focus(); // ставим фокус в поле фразы
		}
	}, [showPhraseList]);

	// следим за задержкой фразы
	useEffect(() => {
		if (debouncedInputPhrase !== phrase.replace(buttonRegexpPattern, "")) {
			sayOrConfirmationActionDataChangeHandler({ text: debouncedInputPhrase, operation: "change" });
		}
	}, [debouncedInputPhrase]);

	// следим за кнопками во фразе
	useEffect(() => {
		buttonsInPhrase.length > 0 && sayOrConfirmationActionDataChangeHandler({ text: inputPhrase, operation: "change" }); // сохранение
	}, [buttonsInPhrase]);

	// обработчик изменения данных действия "Сказать / Подтвердить" (say / confirmation)
	const sayOrConfirmationActionDataChangeHandler = (props: TSaveHandlerProps): void => {
		const arrayCopy = [...arrayPhrases]; // скопировали

		if (props.operation === "change") {
			const phraseWithButtons = buttonsInPhrase.length > 0 ? props.text + buttonsInPhrase.flat().join("") : props.text; // полная фраза с шаблоном кнопок
			if (phraseWithButtons === phrase) return;
			arrayCopy.splice(idxPhrase, 1, phraseWithButtons); // заменили
		} else if (props.operation === "delete") {
			arrayCopy.splice(idxPhrase, 1); // удалили
		} else if (props.operation === "add") {
			arrayCopy.push(...props.text);
		}
		const data: { channel: string; idx: number; action: Partial<IActionPhrase | IActionConfirmation> } = {
			channel,
			idx: idxAction,
			action: { text: arrayCopy },
		};
		handleSaveActionInStore(data);
	};

	// обработчик перефразирования
	const paraphraseHandler = (promptTemplateId?: string): void => {
		dispatch(rephrase({ text: inputPhrase, /* modelGpt: inputModelGPT || undefined, */ promptTemplateId })); // перефразирование
	};

	return (
		<div
			className={cn(styles.phraseBlock, {
				[styles.phraseBlockOpacity]: arrayPhrases.length === idxPhrase && inputPhrase === "", // полупрозрачность последней фразы на добавление
				[styles.phraseBlockNotFirstPhrase]: idxPhrase !== 0, // корректировки отступа и ширины для 2n+ фраз
				[styles.phraseBlockHidden]: arrayPhrases.length === idxPhrase && isDisabled, // скрытие фразы добавления при запрете на редактирование
			})}
		>
			{buttonsInPhrase.length > 0 ? (
				<fieldset
					className={cn(styles.phraseBlockWithButtons, {
						[styles.phraseBlockWithButtonsFocus]: maxRows === 5,
					})}
				>
					<legend>{`${translate("input_phrase")} ${idxPhrase + 1}`}</legend>
					<FormControl fullWidth>
						<CustomTextField
							inputRef={inputRef}
							multiline={multiline}
							disabled={isDisabled}
							maxRows={maxRows}
							value={inputPhrase}
							title={inputPhrase}
							onChange={(e) => setInputPhrase(e.target.value)}
							onFocus={() => {
								setShowPhraseList(true);
								setMaxRows(5);
							}}
							onBlur={() => setMaxRows(2)}
							InputProps={{
								style: {
									padding: "0",
									height: "auto",
								},
								endAdornment: !isDisabled && (
									<InputAdornment
										phrase={phrase}
										arrayPhrases={arrayPhrases}
										idxPhrase={idxPhrase}
										inputPhrase={inputPhrase}
										paraphraseHandler={paraphraseHandler}
										setAnchorForButtonForm={setAnchorForButtonForm}
										setAnchorForParaphrase={setAnchorForParaphrase}
										setSelectButtonType={setSelectButtonType}
										sayOrConfirmationActionDataChangeHandler={sayOrConfirmationActionDataChangeHandler}
									/>
								),
							}}
							sx={{
								"& .MuiInputBase-input": {
									padding: "0 !important",
								},
								// вероятно, лишнее
								// "& .MuiOutlinedInput-root": {
								// 	height: "auto",
								// 	padding: 0,
								// },
								// "& .MuiOutlinedInput-inputMultiline": {
								// 	padding: "0 !important",
								// },
								".MuiOutlinedInput-notchedOutline": { border: "none" },
							}}
						/>
					</FormControl>
					{/* блок кнопок */}
					<ButtonBlock
						buttonsInPhrase={buttonsInPhrase}
						setButtonsInPhrase={setButtonsInPhrase}
						setSelectButtonType={setSelectButtonType}
						setButtonChangeFlg={setButtonChangeFlg}
						setInputButtonName={setInputButtonName}
						setInputButtonData={setInputButtonData}
						setAnchorForButtonForm={setAnchorForButtonForm}
						isDisabled={isDisabled}
					/>
				</fieldset>
			) : (
				<FormControl fullWidth>
					<CustomTextField
						inputRef={inputRef}
						multiline={multiline}
						disabled={isDisabled}
						label={`${translate("input_phrase")} ${idxPhrase + 1}`}
						maxRows={maxRows}
						value={inputPhrase}
						title={inputPhrase}
						onChange={(e) => setInputPhrase(e.target.value)}
						onFocus={() => {
							setShowPhraseList(true);
							setMaxRows(5);
						}}
						onBlur={() => setMaxRows(2)}
						error={!inputPhrase && arrayPhrases.length !== idxPhrase}
						hint={translate("hint_phrase")}
						hintPosition="top"
						sx={{
							"& .MuiInputBase-input": {
								padding: "0 !important",
							},
							// вероятно, лишнее
							// "& .MuiOutlinedInput-root": {
							// 	height: "auto",
							// 	padding: 0,
							// },
							// "& .MuiOutlinedInput-inputMultiline": {
							// 	padding: "0 !important",
							// },
						}}
						InputProps={{
							style: {
								padding: "8px 14px",
								height: "initial",
								minHeight: 33,
							},
							endAdornment: !isDisabled && (
								<InputAdornment
									phrase={phrase}
									arrayPhrases={arrayPhrases}
									idxPhrase={idxPhrase}
									inputPhrase={inputPhrase}
									paraphraseHandler={paraphraseHandler}
									setAnchorForButtonForm={setAnchorForButtonForm}
									setAnchorForParaphrase={setAnchorForParaphrase}
									setSelectButtonType={setSelectButtonType}
									sayOrConfirmationActionDataChangeHandler={sayOrConfirmationActionDataChangeHandler}
								/>
							),
						}}
					/>
				</FormControl>
			)}

			{/* добавление/изменение/удаление кнопки */}
			<ButtonForm
				anchorForButtonForm={anchorForButtonForm}
				setAnchorForButtonForm={setAnchorForButtonForm}
				selectButtonType={selectButtonType}
				setSelectButtonType={setSelectButtonType}
				inputButtonName={inputButtonName}
				setInputButtonName={setInputButtonName}
				inputButtonData={inputButtonData}
				setInputButtonData={setInputButtonData}
				buttonChangeFlg={buttonChangeFlg}
				setButtonChangeFlg={setButtonChangeFlg}
				setButtonsInPhrase={setButtonsInPhrase}
				phrase={phrase}
				channel={channel}
			/>

			{/* перефразирование */}
			<Paraphrasing
				anchorForParaphrase={anchorForParaphrase}
				setAnchorForParaphrase={setAnchorForParaphrase}
				paraphraseHandler={paraphraseHandler}
				sayOrConfirmationActionDataChangeHandler={sayOrConfirmationActionDataChangeHandler}
			/>
		</div>
	);
};

export default memo(Phrase);
